import { createFileRoute, Link, useNavigate, redirect } from "@tanstack/react-router";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUserRoles } from "@/hooks/useUserRoles";
import { resolveLandingRoute } from "@/lib/routeAfterLogin";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { Seal } from "@/components/brand/Seal";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { toast } from "sonner";
import { setViewAs, clearViewAs } from "@/lib/viewAs";

export const Route = createFileRoute("/account")({
  head: () => ({
    meta: [
      { title: "Choose Portal — Currency Thoughts" },
      { name: "robots", content: "noindex" },
    ],
  }),
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      throw redirect({ to: "/login" });
    }
  },
  component: AccountPage,
});

export function AccountPage() {
  const navigate = useNavigate();
  const { loading, userId, isAdmin, isAgent, isClient, portalCount } = useUserRoles();

  // Admins always see the picker (they get 3 view-as tiles).
  // Non-admins with a single portal go straight in.
  useEffect(() => {
    if (loading) return;
    if (isAdmin) return;
    if (portalCount === 1) {
      if (isAgent) navigate({ to: "/agent" });
      else if (isClient && userId) {
        void resolveLandingRoute(userId).then((dest) => {
          window.location.assign(dest);
        });
      }
    }
  }, [loading, portalCount, isAdmin, isAgent, isClient, userId, navigate]);

  const signOut = async () => {
    await supabase.auth.signOut();
    toast.success("Signed out");
    navigate({ to: "/login" });
  };

  const tiles: Array<{
    show: boolean;
    to: "/client" | "/agent" | "/admin";
    title: string;
    top: string;
    bottom: string;
    desc: string;
    viewAs?: "agent" | "client";
  }> = [
    { show: isAdmin, to: "/admin", title: "★", top: "Command", bottom: "Center", desc: "Agency oversight, clients, agents, and activity." },
    { show: isAdmin || isAgent, to: "/agent", title: "A", top: "Strategist", bottom: "Console", desc: isAdmin && !isAgent ? "Preview the Strategist experience (read-only)." : "Assigned leads, pipeline, and commissions.", viewAs: isAdmin ? "agent" : undefined },
    { show: isAdmin || isClient, to: "/client", title: "F", top: "Family", bottom: "Command", desc: isAdmin && !isClient ? "Preview the Family Command Center (read-only)." : "Your policies, vault, and Strategist messages.", viewAs: isAdmin ? "client" : undefined },
  ];

  return (
    <main className="min-h-screen bg-cream-50 px-5 py-24 md:px-10">
      <div className="mx-auto max-w-5xl text-center">
        <Eyebrow>Secure Portal</Eyebrow>
        <h1 className="mt-4 font-display text-[40px] leading-tight text-navy-900 md:text-[56px]">
          Choose your console
        </h1>
        <p className="mx-auto mt-4 max-w-xl font-sans text-[15px] text-charcoal-500">
          You have access to multiple portals. Select the one you'd like to enter.
        </p>

        {loading ? (
          <p className="mt-16 font-sans text-sm text-charcoal-500">Loading…</p>
        ) : portalCount === 0 ? (
          <div className="mx-auto mt-16 max-w-lg border border-bronze-600/40 bg-white p-8">
            <p className="font-sans text-sm text-charcoal-900">
              Your account doesn't have a portal assigned yet. Please contact your Strategist.
            </p>
          </div>
        ) : (
          <div className="mx-auto mt-16 grid max-w-4xl grid-cols-1 gap-8 md:grid-cols-3">
            {tiles.filter((t) => t.show).map((t) => (
              <Link
                key={t.to}
                to={t.to}
                onClick={() => {
                  if (t.viewAs) void setViewAs(t.viewAs);
                  else clearViewAs();
                }}
                className="group flex flex-col items-center gap-5 border border-bronze-600/30 bg-white p-8 transition-colors hover:border-bronze-600 hover:bg-cream-50"
              >
                <Seal title={t.title} topLabel={t.top} bottomLabel={t.bottom} size="md" />
                <p className="font-sans text-[13px] leading-relaxed text-charcoal-500">
                  {t.desc}
                </p>
                <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-bronze-600 group-hover:text-navy-800">
                  Enter →
                </span>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-16">
          <SecondaryButton onClick={signOut}>Sign out</SecondaryButton>
        </div>
      </div>
    </main>
  );
}